import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { TabSdk } from '@techextensor/tab-sdk';
import {
  AppObject,
  Field,
  FieldDataType,
  VisualField,
  VisualFilter,
  VisualSort,
  VisualGroup,
  SQL_OPERATORS,
  AGGREGATE_FUNCTIONS
} from '../../../../../core/interfaces/dev-tool/db-viewer.interface';
import { SqlParserService } from '../../../../../core/services/dev-tool/sql-parser.service';

@Component({
  selector: 'app-visual-query-builder',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './visual-query-builder.component.html',
  styleUrl: './visual-query-builder.component.scss'
})
export class VisualQueryBuilderComponent implements OnInit, OnChanges {
  @Input() sqlQuery: string = '';
  @Input() forceParse: boolean = false; // Trigger parsing when tab is switched
  @Input() isTabActive: boolean = false; // Whether the Visual Builder tab is currently active
  @Input() isExecuting: boolean = false; // Whether query is currently executing
  @Output() sqlQueryChange = new EventEmitter<string>();
  @Output() syncWarning = new EventEmitter<string>();
  @Output() executeQuery = new EventEmitter<void>(); // Emit when execute button is clicked

  // Data Source
  selectedAppObject: AppObject | null = null;
  availableFields: Field[] = [];
  selectedFields: VisualField[] = [];

  // Filters
  filters: VisualFilter[] = [];

  // Sort & Group
  sorts: VisualSort[] = [];
  groups: VisualGroup[] = [];

  // Schema data
  appObjects: AppObject[] = [];
  isLoadingSchema: boolean = false;
  parseWarning: string | null = null;

  // Cached computed values (updated automatically via getters)
  private _selectedFieldsMap: Map<string, VisualField> = new Map();
  private _previewSelect: string = 'No fields selected';
  private _previewWhere: string = 'No filters';
  private _previewOrderBy: string = 'No sorting';
  private _previewGroupBy: string = 'No grouping';
  private _cacheInvalidated: boolean = true;

  // Public getters for template (computed once, cached until data changes)
  get selectedFieldsMap(): Map<string, VisualField> {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._selectedFieldsMap;
  }

  get previewSelect(): string {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._previewSelect;
  }

  get previewWhere(): string {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._previewWhere;
  }

  get previewOrderBy(): string {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._previewOrderBy;
  }

  get previewGroupBy(): string {
    if (this._cacheInvalidated) {
      this.updateCache();
    }
    return this._previewGroupBy;
  }

  // Operators and aggregates from interface constants
  get operators() {
    return SQL_OPERATORS;
  }

  get aggregates() {
    return AGGREGATE_FUNCTIONS;
  }

  private queryChangeSubject = new Subject<void>();
  private isUpdatingFromSQL = false;

  constructor(
    private sqlParserService: SqlParserService
  ) { }

  /**
   * Update all cached computed values
   * Called automatically when cache is invalidated
   */
  private updateCache(): void {
    // Update selectedFieldsMap for O(1) lookups
    this._selectedFieldsMap.clear();
    this.selectedFields.forEach(field => {
      this._selectedFieldsMap.set(field.fieldName, field);
    });

    // Update preview values
    this._previewSelect = this.computePreviewSelect();
    this._previewWhere = this.computePreviewWhere();
    this._previewOrderBy = this.computePreviewOrderBy();
    this._previewGroupBy = this.computePreviewGroupBy();

    this._cacheInvalidated = false;
  }

  /**
   * Invalidate cache when data changes
   */
  private invalidateCache(): void {
    this._cacheInvalidated = true;
  }

  /**
   * Compute SELECT preview
   */
  private computePreviewSelect(): string {
    if (this.selectedFields.length === 0) return 'No fields selected';

    return this.selectedFields.map(f => {
      let expr = f.fieldName;
      if (f.aggregate !== 'None') {
        expr = `${f.aggregate}(${expr})`;
      }
      if (f.alias) {
        expr += ` AS ${f.alias}`;
      }
      return expr;
    }).join(', ');
  }

  /**
   * Compute WHERE preview
   */
  private computePreviewWhere(): string {
    if (this.filters.length === 0) return 'No filters';

    return this.filters.map((f, i) => {
      let condition = f.fieldName;
      if (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL') {
        condition += ` ${f.operator}`;
      } else {
        condition += ` ${f.operator} ${f.value || '?'}`;
      }
      if (i > 0) {
        condition = `${f.conjunction} ${condition}`;
      }
      return condition;
    }).join(' ');
  }

  /**
   * Compute ORDER BY preview
   */
  private computePreviewOrderBy(): string {
    if (this.sorts.length === 0) return 'No sorting';
    return this.sorts.map(s => `${s.fieldName} ${s.direction}`).join(', ');
  }

  /**
   * Compute GROUP BY preview
   */
  private computePreviewGroupBy(): string {
    if (this.groups.length === 0) return 'No grouping';
    return this.groups.map(g => g.fieldName).join(', ');
  }

  ngOnInit(): void {
    this.loadSchema();

    // Debounce visual builder changes to SQL (300ms as per requirements)
    this.queryChangeSubject.pipe(
      debounceTime(300)
    ).subscribe(() => {
      if (!this.isUpdatingFromSQL) {
        this.generateSQL();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sqlQuery']) {
      // SQL changed externally, parse and populate visual builder
      // Only parse if we have schema loaded and SQL is not empty
      if (this.appObjects.length > 0 && this.sqlQuery && this.sqlQuery.trim()) {
        this.parseSQLToVisual();
      }
    }

    if (changes['forceParse'] && changes['forceParse'].currentValue) {
      // Force parse when tab is switched to visual builder
      if (this.appObjects.length > 0 && this.sqlQuery && this.sqlQuery.trim()) {
        this.parseSQLToVisual();
      }
    }

    if (changes['isTabActive']) {
      // When tab becomes active, parse SQL and show warnings if needed
      if (changes['isTabActive'].currentValue) {
        // Tab just became active - parse SQL to check for warnings
        if (this.appObjects.length > 0 && this.sqlQuery && this.sqlQuery.trim()) {
          // Re-parse to check for warnings now that tab is active
          this.parseSQLToVisual();
        }
      } else {
        // Tab became inactive - clear warning to prevent showing on next activation
        this.parseWarning = null;
      }
    }
  }

  loadSchema(): void {
    this.isLoadingSchema = true;
    try {
      // Get all app objects from TabSdk metadata utils
      const appObjects = TabSdk.metadataUtils.getAllAppObjects();

      // Convert TabSdk AppObject structure to our interface structure
      this.appObjects = appObjects.map((appObj: any) => ({
        id: appObj.ID,
        name: appObj.ObjectName,
        displayName: appObj.DisplayName,
        fields: appObj.fields().map((field: any) => ({
          id: field.ID,
          name: field.FieldName,
          displayName: field.DisplayName,
          dataType: field.FieldType?.DataType || 0,
          isPrimaryKey: field.IsPrimaryKey || false,
          isRequired: field.IsRequired || false,
          isLookup: field.FieldType?.DataType === 17,
          lookupDetails: field.FieldType?.DataType === 17 && field.LookUpDetails ? {
            targetObjectName: field.LookUpDetails.LookupAppObjectName,
            targetDisplayField: field.LookUpDetails.LookupAppObjectDisplayFieldName
          } : undefined
        }))
      }));

      this.isLoadingSchema = false;

      // Only parse SQL if tab is active (prevents parsing on startup)
      if (this.isTabActive && this.sqlQuery && this.sqlQuery.trim()) {
        // Use setTimeout to ensure component is fully initialized
        setTimeout(() => {
          this.parseSQLToVisual();
        }, 0);
      }
    } catch (error) {
      console.error('Error loading schema:', error);
      this.isLoadingSchema = false;
    }
  }

  onAppObjectChange(): void {
    if (this.selectedAppObject) {
      this.availableFields = this.selectedAppObject.fields;

      // Clear selected fields that don't belong to this object
      this.selectedFields = this.selectedFields.filter(f =>
        this.availableFields.some(af => af.name === f.fieldName)
      );
    } else {
      this.availableFields = [];
      this.selectedFields = [];
    }

    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  onFieldToggle(field: Field): void {
    const existingIndex = this.selectedFields.findIndex(f => f.fieldName === field.name);

    if (existingIndex >= 0) {
      // Remove field
      this.selectedFields.splice(existingIndex, 1);
    } else {
      // Add field
      this.selectedFields.push({
        id: this.generateId(),
        fieldName: field.name,
        alias: '',
        aggregate: 'None',
        selected: true
      });
    }

    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  onFieldChange(): void {
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  /**
   * Check if field is selected - optimized with Map lookup O(1)
   */
  isFieldSelected(fieldName: string): boolean {
    return this.selectedFieldsMap.has(fieldName);
  }

  /**
   * Get selected field - optimized with Map lookup O(1)
   */
  getSelectedField(fieldName: string): VisualField | undefined {
    return this.selectedFieldsMap.get(fieldName);
  }

  addFilter(): void {
    if (!this.selectedAppObject || this.availableFields.length === 0) {
      return;
    }

    this.filters.push({
      id: this.generateId(),
      fieldName: this.availableFields[0].name,
      operator: '=',
      value: '',
      conjunction: 'AND'
    });
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  removeFilter(filterId: string): void {
    const index = this.filters.findIndex(f => f.id === filterId);
    if (index >= 0) {
      this.filters.splice(index, 1);
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  onFilterChange(): void {
    // Remove filters with empty field names
    this.filters = this.filters.filter(f => f.fieldName && f.fieldName.trim() !== '');
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  onFilterOperatorChange(filter: VisualFilter): void {
    // Clear value when operator changes to IS NULL or IS NOT NULL
    if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
      filter.value = '';
    }
    this.onFilterChange();
  }

  getFilterInputType(fieldName: string): string {
    if (!fieldName || !this.selectedAppObject) return 'text';

    const field = this.selectedAppObject.fields.find(f => f.name === fieldName);
    if (!field) return 'text';

    switch (field.dataType) {
      case FieldDataType.Number:
      case FieldDataType.Decimal:
        return 'number';
      case FieldDataType.DateTime:
      case FieldDataType.TimeStamp:
        return 'datetime-local';
      case FieldDataType.Boolean:
        return 'checkbox';
      default:
        return 'text';
    }
  }

  isFilterValueRequired(operator: string): boolean {
    return operator !== 'IS NULL' && operator !== 'IS NOT NULL';
  }

  addSort(): void {
    this.sorts.push({
      id: this.generateId(),
      fieldName: this.availableFields.length > 0 ? this.availableFields[0].name : '',
      direction: 'ASC'
    });
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  removeSort(sortId: string): void {
    const index = this.sorts.findIndex(s => s.id === sortId);
    if (index >= 0) {
      this.sorts.splice(index, 1);
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  moveSortUp(index: number): void {
    if (index > 0) {
      const temp = this.sorts[index];
      this.sorts[index] = this.sorts[index - 1];
      this.sorts[index - 1] = temp;
      // Force change detection
      this.sorts = [...this.sorts];
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  moveSortDown(index: number): void {
    if (index < this.sorts.length - 1) {
      const temp = this.sorts[index];
      this.sorts[index] = this.sorts[index + 1];
      this.sorts[index + 1] = temp;
      // Force change detection
      this.sorts = [...this.sorts];
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  onSortChange(): void {
    // Remove sorts with empty field names
    this.sorts = this.sorts.filter(s => s.fieldName && s.fieldName.trim() !== '');
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  addGroup(): void {
    const fieldName = this.availableFields.length > 0 ? this.availableFields[0].name : '';
    if (fieldName && !this.groups.some(g => g.fieldName === fieldName)) {
      this.groups.push({
        id: this.generateId(),
        fieldName: fieldName
      });
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  removeGroup(groupId: string): void {
    const index = this.groups.findIndex(g => g.id === groupId);
    if (index >= 0) {
      this.groups.splice(index, 1);
      this.invalidateCache();
      this.triggerSQLUpdate();
    }
  }

  onGroupChange(): void {
    // Remove groups with empty field names
    this.groups = this.groups.filter(g => g.fieldName && g.fieldName.trim() !== '');
    // When grouping, automatically set aggregates for non-grouped fields
    this.updateAggregatesForGrouping();
    this.invalidateCache();
    this.triggerSQLUpdate();
  }

  updateAggregatesForGrouping(): void {
    if (this.groups.length > 0) {
      // For grouped fields, remove aggregates
      this.selectedFields.forEach(field => {
        if (this.groups.some(g => g.fieldName === field.fieldName)) {
          if (field.aggregate !== 'None') {
            field.aggregate = 'None';
          }
        }
      });
    }
  }

  parseSQLToVisual(): void {
    if (!this.sqlQuery || !this.sqlQuery.trim()) {
      return;
    }

    this.isUpdatingFromSQL = true;
    // Only clear warning if we're actively parsing (not on initial load)
    if (this.isTabActive) {
      this.parseWarning = null;
    }

    try {
      const parsed = this.sqlParserService.parseQuery(this.sqlQuery);

      // Check for unsupported features - only set warning if tab is active
      if (parsed.Joins && parsed.Joins.length > 0) {
        // Only set and show warning if the tab is currently active
        if (this.isTabActive) {
          this.parseWarning = 'JOINs are not fully supported in Visual Builder. Some features may not be editable.';
          this.syncWarning.emit(this.parseWarning);
        }
        // Don't set parseWarning if tab is not active (prevents showing on startup)
      }

      // Set AppObject from FROM clause
      if (parsed.SelectedFields && parsed.SelectedFields.length > 0) {
        // Try to find the table from the query
        const fromMatch = this.sqlQuery.match(/FROM\s+(\w+)/i);
        if (fromMatch) {
          const tableName = fromMatch[1];
          this.selectedAppObject = this.appObjects.find(obj =>
            obj.name.toLowerCase() === tableName.toLowerCase()
          ) || null;

          if (this.selectedAppObject) {
            this.availableFields = this.selectedAppObject.fields;
          }
        }
      }

      // Parse selected fields
      this.selectedFields = parsed.SelectedFields ? parsed.SelectedFields.map((fieldExpr: string) => {
        const aliasMatch = fieldExpr.match(/\s+AS\s+(\w+)$/i);
        const alias = aliasMatch ? aliasMatch[1] : '';
        const fieldWithoutAlias = aliasMatch ? fieldExpr.substring(0, aliasMatch.index).trim() : fieldExpr.trim();

        // Check for aggregate functions
        const aggregateMatch = fieldWithoutAlias.match(/^(COUNT|SUM|AVG|MIN|MAX)\s*\(/i);
        const aggregate = aggregateMatch ? aggregateMatch[1].toUpperCase() as any : 'None';

        // Extract field name
        let fieldName = fieldWithoutAlias;
        if (aggregate !== 'None') {
          const fieldMatch = fieldWithoutAlias.match(/\(([^)]+)\)/);
          fieldName = fieldMatch ? fieldMatch[1].trim() : fieldWithoutAlias;
        }

        return {
          id: this.generateId(),
          fieldName: this.extractFieldName(fieldName),
          alias,
          aggregate,
          selected: true
        };
      }) : [];

      // Parse filters
      this.filters = (parsed.WhereClause?.Filters || []).map((filter: any, index: number) => ({
        id: this.generateId(),
        fieldName: this.extractFieldName(filter.FieldName),
        operator: this.getOperatorFromNumber(filter.Operator),
        value: filter.Value || '',
        conjunction: index === 0 ? 'AND' : (filter.Conjunction === 2 ? 'OR' : 'AND')
      }));

      // Parse sorts
      this.sorts = (parsed.Sort || []).map((sort: any) => ({
        id: this.generateId(),
        fieldName: this.extractFieldName(sort.FieldName),
        direction: sort.Direction
      }));

      // Parse groups
      this.groups = (parsed.GroupBy || []).map((groupField: string) => ({
        id: this.generateId(),
        fieldName: this.extractFieldName(groupField)
      }));

      // Invalidate cache after parsing
      this.invalidateCache();

    } catch (error: any) {
      // Only set warning if tab is active (to avoid showing on startup)
      if (this.isTabActive) {
        this.parseWarning = `Unable to fully parse SQL query: ${error.message}. Some features may not be editable.`;
        this.syncWarning.emit(this.parseWarning);
      }
      console.warn('Error parsing SQL to visual:', error);
    } finally {
      this.isUpdatingFromSQL = false;
    }
  }

  generateSQL(): void {
    if (!this.selectedAppObject || this.selectedFields.length === 0) {
      this.sqlQueryChange.emit('');
      return;
    }

    const sqlParts: string[] = [];

    // SELECT clause
    sqlParts.push('SELECT');
    const selectFields = this.selectedFields.map(field => {
      let fieldExpr = field.fieldName;

      // Add aggregate if specified
      if (field.aggregate !== 'None') {
        fieldExpr = `${field.aggregate}(${fieldExpr})`;
      }

      // Add alias if specified
      if (field.alias && field.alias.trim()) {
        fieldExpr += ` AS ${field.alias}`;
      }

      return `    ${fieldExpr}`;
    });
    sqlParts.push(selectFields.join(',\n'));

    // FROM clause
    sqlParts.push(`FROM ${this.selectedAppObject.name}`);

    // WHERE clause
    const whereConditions = this.buildWhereConditions();
    if (whereConditions.length > 0) {
      sqlParts.push('WHERE ' + whereConditions.join(' '));
    }

    // GROUP BY clause
    const validGroups = this.groups.filter(g => g.fieldName && g.fieldName.trim() !== '');
    if (validGroups.length > 0) {
      sqlParts.push('GROUP BY ' + validGroups.map(g => g.fieldName).join(', '));
    }

    // ORDER BY clause
    const validSorts = this.sorts.filter(s => s.fieldName && s.fieldName.trim() !== '');
    if (validSorts.length > 0) {
      sqlParts.push('ORDER BY ' + validSorts.map(s => `${s.fieldName} ${s.direction}`).join(', '));
    }

    this.sqlQueryChange.emit(sqlParts.join('\n'));
  }

  triggerSQLUpdate(): void {
    this.queryChangeSubject.next();
  }

  getOperatorFromNumber(opNum: number): string {
    return this.sqlParserService.getSQLOperatorFromNumber(opNum);
  }

  clearBuilder(): void {
    TabSdk.ui.confirm({
      message: 'Are you sure you want to clear the visual builder? This will reset all selections.',
      title: 'Clear editor'
    }).then(confirmed => {
      if (confirmed) {
        this.selectedAppObject = null;
        this.availableFields = [];
        this.selectedFields = [];
        this.filters = [];
        this.sorts = [];
        this.groups = [];
        this.parseWarning = null;
        this.invalidateCache();
        this.sqlQueryChange.emit('');
      }
    });
  }

  onExecuteQuery(): void {
    // First, ensure SQL is up to date
    this.generateSQL();
    // Emit event to parent to execute query
    this.executeQuery.emit();
  }

  generateId(): string {
    return 'vb_' + Math.random().toString(36).substr(2, 9);
  }

  // ==================== UTILITY METHODS ====================

  /**
   * Format value for SQL query based on data type and operator
   */
  private formatSQLValue(value: string, operator: string, fieldName: string): string {
    if (!value || !value.trim()) return '';

    const trimmedValue = value.trim();

    // Handle LIKE operator
    if (operator === 'LIKE') {
      return `'%${trimmedValue}%'`;
    }

    // Handle IN/NOT IN operators
    if (operator === 'IN' || operator === 'NOT IN') {
      const values = trimmedValue.split(',').map(v => {
        const val = v.trim();
        if (!val) return null;
        return isNaN(Number(val)) ? `'${val}'` : val;
      }).filter(v => v !== null);

      return values.length > 0 ? `(${values.join(', ')})` : '';
    }

    // Handle date/datetime
    const inputType = this.getFilterInputType(fieldName);
    if (inputType === 'datetime-local' || inputType === 'date') {
      return `'${trimmedValue}'`;
    }

    // Handle parameters
    if (trimmedValue.startsWith('@')) {
      return trimmedValue;
    }

    // Handle numbers vs strings
    if (isNaN(Number(trimmedValue))) {
      return `'${trimmedValue}'`;
    }

    return trimmedValue;
  }

  /**
   * Build WHERE clause conditions array
   */
  private buildWhereConditions(): string[] {
    const validFilters = this.filters.filter(f =>
      f.fieldName &&
      f.fieldName.trim() !== '' &&
      (f.operator === 'IS NULL' || f.operator === 'IS NOT NULL' || (f.value && f.value.trim() !== ''))
    );

    const conditions: string[] = [];

    validFilters.forEach((filter, index) => {
      if (index > 0) {
        conditions.push(filter.conjunction);
      }

      let condition = filter.fieldName;

      if (filter.operator === 'IS NULL' || filter.operator === 'IS NOT NULL') {
        condition += ` ${filter.operator}`;
      } else {
        const formattedValue = this.formatSQLValue(filter.value || '', filter.operator, filter.fieldName);
        if (!formattedValue) return; // Skip empty values
        condition += ` ${filter.operator} ${formattedValue}`;
      }

      conditions.push(condition);
    });

    return conditions;
  }

  /**
   * Extract field name from field expression (remove table prefix if present)
   */
  private extractFieldName(fieldExpr: string): string {
    let fieldName = fieldExpr;
    if (fieldName.includes('.')) {
      fieldName = fieldName.split('.')[1];
    }
    return fieldName;
  }
}

